import { setupForDark } from '@theme';
import classNames from 'classnames';
import * as _ from 'lodash';
import React, { useEffect, useMemo, useRef } from 'react';

interface InputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value' | 'step' | 'min' | 'onChange'
> {
  type: string;
  value?: string | number;
  step?: string;
  min?: string;
  fitContent?: boolean;
  hideArrows?: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

// FIXME: forwardRef is deprecated in react 19
// https://react.dev/blog/2024/12/05/react-19#ref-as-a-prop
// https://react.dev/reference/react/forwardRef
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type,
      placeholder,
      value = null,
      step = null,
      min = null,
      disabled = false,
      className = '',
      autoFocus = false,
      fitContent = false,
      hideArrows = false,
      maxLength = undefined,
      onChange,
      onKeyDown = () => {},
      onBlur = () => {},
      onFocus = () => {},
      ...rest
    },
    ref
  ) => {
    let extraProps = {
      onKeyDown: onKeyDown,
      onBlur: onBlur,
      onFocus: onFocus,
      ...(step ? { step: step } : {}),
      ...(min ? { min: min } : {}),
      ...(value !== null && value !== undefined ? { value: value } : { value: '' })
    };

    const inputRef = useRef<HTMLInputElement>(null);
    React.useImperativeHandle(ref, () => inputRef.current!);

    useEffect(() => {
      if (autoFocus && !disabled) {
        inputRef.current?.focus();
      }
    }, [inputRef?.current, autoFocus, disabled]);

    const style = useMemo(() => {
      if (fitContent && _.isString(value)) {
        let width = Math.max((value as string).length, 1) + 2;
        return { width: `${width}ch` };
      }
      return {};
    }, [fitContent, value]);

    return (
      <input
        {...rest}
        maxLength={maxLength}
        ref={inputRef}
        className={classNames(
          'w-full',
          'p-sm rounded-xs border-[1px] outline-none',
          'P2R',
          setupForDark('text-primary border-dark bg-text-input-default'),
          setupForDark(
            'disabled:text-tertiary disabled:border-text-input-disabled disabled:bg-text-input-disabled'
          ),
          hideArrows && type === 'number' && 'hide-input-arrows',
          className
        )}
        disabled={disabled}
        onChange={onChange}
        type={type}
        placeholder={placeholder}
        style={style}
        {...extraProps}
      />
    );
  }
);

Input.displayName = 'Input';

export default Input;
